import Component from '@glimmer/component'
import type School from 'district-ui-client/models/school'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type SubscriptionCoordinator from 'district-ui-client/models/subscription-coordinator'
import { TableContainer, Table, TRHead, TBody, TH, TD } from 'district-ui-client/components/table'
import { t, type IntlService } from 'ember-intl'
import { ThemedButton, ThemedLink } from 'district-ui-client/components/themed-button'
import PhosphorIcon from 'district-ui-client/components/phosphor-icon'
import { tracked } from '@glimmer/tracking'
import { on } from '@ember/modifier'
import type { TOC } from '@ember/component/template-only'
import {
  ThemedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  CloseButton,
} from 'district-ui-client/components/themed-modal'
import { fn } from '@ember/helper'
import { or } from 'ember-truth-helpers'
import { trackedFunction } from 'reactiveweb/function'
import type Teacher from 'district-ui-client/models/teacher'
import type Store from 'ember-data/store'
import { service } from '@ember/service'
import { MultiSelect } from 'district-ui-client/components/multi-select'
import { hasId } from 'district-ui-client/utils/type-guards'
import { task } from 'ember-concurrency'
import type FlashQueueService from 'district-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import { Panel, PanelHeader, PanelTitle, PanelBody } from 'district-ui-client/components/section'
import { Pill } from 'district-ui-client/components/pills'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import type FeaturesService from 'district-ui-client/services/features'

export interface SchoolSubcosSignature {
  Args: {
    school: School
    subscriptionType: SubscriptionType
  }
  Element: HTMLElement
}

export class SchoolSubcos extends Component<SchoolSubcosSignature> {
  @tracked
  subcoToRemove: SubscriptionCoordinator | null = null

  @tracked showAddSubcoModal: boolean = false

  @service store!: Store
  @service intl!: IntlService
  @service flashQueue!: FlashQueueService
  @service log!: Log
  @service externalRostering!: ExternalRosteringService
  @service features!: FeaturesService

  get managesAdminsInRoster(): boolean {
    return this.features.isEnabled('dui_roster_manage_admins') && this.externalRostering.rostersInRosterApp
  }

  get canManageInPage(): boolean {
    return !this.externalRostering.isEnabled && !this.managesAdminsInRoster
  }

  get manageAdminsUrl(): string {
    const schoolId = this.args.school.id!
    return this.externalRostering.oneRosterUrl(schoolId, 'manage-admins', this.args.subscriptionType)
  }

  get subcos() {
    return (
      this.args.school.subscriptionCoordinators?.filter((sc: SubscriptionCoordinator) =>
        sc.isForSubscriptionType(this.args.subscriptionType),
      ) || []
    )
  }

  get disableRemoveSubco() {
    return this.subcos.length < 2
  }

  onShowRemove = (subco: SubscriptionCoordinator) => {
    this.subcoToRemove = subco
  }

  closeRemoveModal = () => {
    this.subcoToRemove = null
  }

  onCancelRemove = () => {
    this.closeRemoveModal()
  }

  onConfirmRemove = async () => {
    const subcoToRemove = this.subcoToRemove
    try {
      const teacher = subcoToRemove?.teacher
      const promise = subcoToRemove?.destroyRecord()
      this.closeRemoveModal()
      await promise
      this.flashQueue.addSuccess({
        title: this.intl.t('components.school.subcos.removeSubcoSuccessMessage'),
        subtitle: this.intl.t('components.school.subcos.removeSubcoSuccessMessageDetail', {
          teacherName: teacher?.fullName,
          schoolName: this.args.school.name,
          htmlSafe: true,
        }),
      })
    } catch {
      subcoToRemove?.rollbackAttributes() // restore previous state
    }
  }

  addSubcoTask = task({ drop: true }, async (teachers: Teacher[]) => {
    this.closeAddSubcoModal()

    const promises = teachers.map(async (teacher): Promise<[true, SubscriptionCoordinator] | [false, unknown]> => {
      let subco: SubscriptionCoordinator | null = null
      try {
        subco = this.store.createRecord<SubscriptionCoordinator>('subscription-coordinator', {
          school: this.args.school,
          teacher,
          product: this.args.subscriptionType,
        })
        subco = await subco.save()
        return [true, subco]
      } catch (error) {
        if (subco) subco.unloadRecord()
        return [false, error]
      }
    })

    const savedSubcos = await Promise.all(promises)
    for (const [success, subcoOrError] of savedSubcos) {
      if (!success) {
        this.log.error('failed to save new subco', subcoOrError as Error | undefined)
        this.flashQueue.addFail({
          title: this.intl.t('components.school.subcos.addSubcoFailedMessage'),
          subtitle: (subcoOrError as Error)?.message,
        })
      } else {
        const subco = subcoOrError
        this.flashQueue.addSuccess({
          title: this.intl.t('components.school.subcos.addSubcoSuccessMessage'),
          subtitle: this.intl.t('components.school.subcos.addSubcoSuccessMessageDetail', {
            teacherName: subco.teacher?.fullName,
            schoolName: this.args.school.name,
            htmlSafe: true,
          }),
        })
      }
    }
  })

  openAddSubcoModal = () => {
    this.showAddSubcoModal = true
  }

  closeAddSubcoModal = () => {
    this.showAddSubcoModal = false
  }

  <template>
    <Panel data-test-school-subcos ...attributes>
      <PanelHeader class="flex items-center justify-between">
        <PanelTitle>{{t "components.school.subcos.panelTitle"}}</PanelTitle>
        {{#if this.managesAdminsInRoster}}
          <ThemedLink
            @style="theme"
            href={{this.manageAdminsUrl}}
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center"
            data-test-add-subcos-roster-link
          >
            <PhosphorIcon @icon="phosphor-plus" class="mr-2" />
            {{t "components.school.subcos.add"}}
            <PhosphorIcon @icon="phosphor-arrow-square-out-bold" class="ml-2 shrink-0" />
          </ThemedLink>
        {{else if this.canManageInPage}}
          <ThemedButton
            @style="theme"
            {{on "click" this.openAddSubcoModal}}
            class="flex items-center"
            data-test-add-subcos-button
          >
            <PhosphorIcon @icon="phosphor-plus" class="mr-2" />
            {{t "components.school.subcos.add"}}
          </ThemedButton>
        {{/if}}
      </PanelHeader>
      <PanelBody @tight={{true}}>
        <TableContainer>
          <Table>
            <thead>
              <TRHead class="select-none">
                <TH>{{t "components.school.subcos.subscriptionCoordinator"}}</TH>
                <TH>{{t "components.school.subcos.loginAs"}}</TH>
                {{#if this.canManageInPage}}
                  <TH class={{if this.disableRemoveSubco "cursor-not-allowed text-neutral-200"}}>{{t
                      "components.school.subcos.remove"
                    }}</TH>
                {{/if}}
              </TRHead>
            </thead>
            <TBody>
              {{#each this.subcos as |subco|}}
                <tr>
                  <TD>{{subco.teacher.fullName}} ({{subco.teacher.email}})</TD>
                  <TD>
                    <ThemedLink data-test-subco-sso-link @style="neutral" href={{subco.teacher.subcoLoginPath}}>
                      <PhosphorIcon @icon="phosphor-sign-out" />
                      {{t "components.school.subcos.loginAsName" name=subco.teacher.fullName}}
                    </ThemedLink>
                  </TD>
                  {{#if this.canManageInPage}}
                    <TD class={{if this.disableRemoveSubco "cursor-not-allowed"}}>
                      <div class="group flex items-center space-x-2">
                        <ThemedButton
                          @style="destructive"
                          @disabled={{or this.disableRemoveSubco subco.isSaving}}
                          class="!p-2"
                          data-test-remove-subco-button
                          {{on "click" (fn this.onShowRemove subco)}}
                        >
                          {{#if subco.isSaving}}
                            <PhosphorIcon @icon="phosphor-spinner" class="animate-spin" />
                          {{else}}
                            <PhosphorIcon @icon="phosphor-x-circle" />
                          {{/if}}
                        </ThemedButton>
                        {{#if this.disableRemoveSubco}}
                          <div
                            class="invisible inline-block text-sm text-neutral-300 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100"
                          >
                            {{t "components.school.subcos.mustHaveOneSubco"}}
                          </div>
                        {{/if}}
                      </div>
                    </TD>
                  {{/if}}
                </tr>
              {{/each}}
            </TBody>
          </Table>
        </TableContainer>
        <RemoveSubcoConfirmModal
          @subco={{this.subcoToRemove}}
          @onCancel={{this.onCancelRemove}}
          @onConfirm={{this.onConfirmRemove}}
        />
        {{#if this.showAddSubcoModal}}
          <AddSubcoModal
            @school={{@school}}
            @subscriptionType={{@subscriptionType}}
            @subcos={{this.subcos}}
            @onCancel={{this.closeAddSubcoModal}}
            @onSubmit={{this.addSubcoTask.perform}}
          />
        {{/if}}
      </PanelBody>
    </Panel>
  </template>
}

interface RemoveSubcoConfirmModalSignature {
  Element: HTMLDivElement
  Args: {
    subco: Nullable<SubscriptionCoordinator>
    onCancel: () => void
    onConfirm: () => void
  }
}

export const RemoveSubcoConfirmModal: TOC<RemoveSubcoConfirmModalSignature> = <template>
  {{#if @subco}}
    <ThemedModal @show={{true}} @close={{@onCancel}}>
      <ModalHeader>
        <ModalHeading>{{t "components.school.subcos.confirmRemovalTitle"}}</ModalHeading>
        <CloseButton @close={{@onCancel}} />
      </ModalHeader>
      <ModalBody class="space-y-4 text-sm">
        <div>
          {{t
            "components.school.subcos.confirmRemovalBody"
            name=@subco.teacher.fullName
            schoolName=@subco.school.name
            htmlSafe=true
          }}
        </div>
      </ModalBody>
      <ModalFooter>
        <ThemedButton data-test-confirm-cancel class="min-w-24" @style="text" {{on "click" @onCancel}}>
          {{t "cancel"}}
        </ThemedButton>
        <ThemedButton data-test-confirm-submit class="min-w-24" @style="destructive" {{on "click" @onConfirm}}>
          {{t "components.school.subcos.confirmRemovalLabel" name=@subco.teacher.fullName}}
        </ThemedButton>
      </ModalFooter>
    </ThemedModal>
  {{/if}}
</template>

export interface AddSubcoModalSignature {
  Element: HTMLDivElement
  Args: {
    school: School
    subcos: SubscriptionCoordinator[]
    subscriptionType: SubscriptionType
    onCancel: () => void
    onSubmit: (teachers: Teacher[]) => void
  }
}

export class AddSubcoModal extends Component<AddSubcoModalSignature> {
  @service store!: Store

  @tracked selectedIds: string[] = []

  get teachers(): (Teacher & { id: string })[] {
    // filter out teachers who are already subcos for this subscription type
    return (
      this.fetchTeachers.value
        ?.filter(hasId)
        .filter(
          (teacher: Teacher) =>
            this.args.school.subscriptionCoordinators === undefined ||
            this.args.school.subscriptionCoordinators.every(
              (subco) =>
                subco.teacher === undefined ||
                subco.teacher.id !== teacher.id ||
                !subco.isForSubscriptionType(this.args.subscriptionType),
            ),
        ) ?? []
    )
  }

  get options() {
    return this.teachers.map((teacher) => ({ value: teacher.id, label: teacher.fullName }))
  }

  get selectedTeachers() {
    return this.teachers.filter((teacher) => this.selectedIds.includes(teacher.id))
  }

  onSelect = (selectedIds: string[]) => {
    this.selectedIds = selectedIds
  }

  onRemove = (teacherId: string) => {
    this.selectedIds = this.selectedIds.filter((id) => id !== teacherId)
  }

  fetchTeachers = trackedFunction(this, async () => {
    const schoolId = this.args.school.id
    await Promise.resolve() // disconnect tracking before store calls

    return await this.store.query<Teacher>('teacher', {
      scope: `schools/${schoolId}`,
      // @ts-expect-error - include types not correct
      include: 'school', // need teacher.school relation
    })
  })

  get noTeachersSelected() {
    return this.selectedIds.length === 0
  }

  get someTeachersSelected() {
    return this.selectedIds.length > 0
  }

  <template>
    <ThemedModal data-test-add-subco-modal @show={{true}} @close={{@onCancel}}>
      <ModalHeader>
        <ModalHeading>{{t "components.school.subcos.addSubcoTitle"}}</ModalHeading>
        <CloseButton @close={{@onCancel}} />
      </ModalHeader>
      <ModalBody class="w-[600px]">
        <div>
          {{t "components.school.subcos.addSubcoBody" schoolName=@school.name htmlSafe=true}}
        </div>
        {{#if this.someTeachersSelected}}
          <div class="mt-4 flex flex-wrap gap-2">
            {{#each this.selectedTeachers as |teacher|}}
              <Pill @theme="neutral" {{on "click" (fn this.onRemove teacher.id)}} class="cursor-pointer">
                <span>{{teacher.fullName}}</span>
                <PhosphorIcon @icon="phosphor-x-bold" class="h-4 w-4" />
              </Pill>
            {{/each}}
          </div>
        {{/if}}
        <MultiSelect
          data-test-select-subcos
          class="my-4"
          @style="neutral"
          @placeholder={{if
            this.fetchTeachers.isPending
            (t "loading")
            (t "components.school.subcos.addSubcoPlaceholder")
          }}
          @searchPlaceholder={{t "components.school.subcos.addSubcoSearchPlaceholder"}}
          @selectedItems={{this.selectedIds}}
          @options={{this.options}}
          @onSelect={{this.onSelect}}
          @matchTriggerWidth={{true}}
        />
      </ModalBody>
      <ModalFooter>
        <ThemedButton data-test-cancel class="min-w-24" @style="text" {{on "click" @onCancel}}>{{t
            "cancel"
          }}</ThemedButton>

        <ThemedButton
          data-test-submit
          class="min-w-24"
          @disabled={{this.noTeachersSelected}}
          @style="theme"
          {{on "click" (fn @onSubmit this.selectedTeachers)}}
        >
          {{t "components.school.subcos.addSubcoLabel"}}
        </ThemedButton>
      </ModalFooter>
    </ThemedModal>
  </template>
}
